/**
 * Shared streaming AI client.
 *
 * Phase 51.A-Lab — single client used by both the global ChatWidget and
 * the Lab Assistant (and any future AI surface — Planner, Code-Gen, etc.).
 *
 * Wraps the FastAPI SSE endpoints `/api/chat/stream` and
 * `/api/lab/chat/stream`. Both endpoints emit:
 *   event: token   data: {text: "..."}
 *   event: done    data: {}
 *   event: error   data: {msg: "..."}
 *
 * Returns a `cancel()` handle so the caller can abort an in-flight
 * generation (used by the "stop" button in the UI).
 *
 * Why fetch+ReadableStream and not EventSource?
 *   • EventSource can't send POST bodies or custom Authorization headers.
 *   • fetch+ReadableStream is the modern pattern (same as ChatGPT, Claude).
 */
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const ENDPOINTS = {
  operator: `${BACKEND}/api/chat/stream`,
  lab:      `${BACKEND}/api/lab/chat/stream`,
};

/**
 * Stream tokens from one of the AI endpoints.
 *
 * @param {Object} opts
 * @param {"operator"|"lab"|string} opts.kind         which endpoint to use
 * @param {string}   opts.sessionId
 * @param {string}   opts.message
 * @param {string}   opts.token                       JWT
 * @param {(t:string)=>void} opts.onToken             called for every token chunk
 * @param {()=>void}        [opts.onDone]
 * @param {(err:string)=>void} [opts.onError]
 * @returns {{cancel: ()=>void, promise: Promise<void>}}
 */
export function streamChat({ kind, sessionId, message, token, onToken, onDone, onError, truncateAt }) {
  const url = ENDPOINTS[kind] || kind; // allow full URL override
  const ctrl = new AbortController();

  const promise = (async () => {
    try {
      // Iter 110 — when the UI is editing a prior user message, pass
      // `truncate_at` so the server trims the conversation to that index
      // BEFORE streaming the new reply. The new user+assistant pair
      // then lands at the correct slot and the stale reply vanishes.
      const payload = { session_id: sessionId, message };
      if (truncateAt != null && truncateAt >= 0) payload.truncate_at = truncateAt;
      const res = await fetch(url, {
        method: 'POST',
        signal: ctrl.signal,
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'text/event-stream',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok || !res.body) {
        const body = await res.text().catch(() => '');
        onError?.(`HTTP ${res.status}: ${body.slice(0, 200) || 'no body'}`);
        return;
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = '';
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        // SSE events are separated by blank lines.
        let idx;
        while ((idx = buf.indexOf('\n\n')) !== -1) {
          const raw = buf.slice(0, idx);
          buf = buf.slice(idx + 2);
          const evMatch = raw.match(/^event:\s*(\S+)/m);
          const dataMatch = raw.match(/^data:\s*(.*)$/m);
          if (!dataMatch) continue;
          let payload = {};
          try { payload = JSON.parse(dataMatch[1]); } catch { /* keep empty */ }
          const ev = evMatch?.[1] || 'message';
          if (ev === 'token' && payload.text != null) onToken?.(payload.text);
          else if (ev === 'done')  onDone?.();
          else if (ev === 'error') onError?.(payload.msg || 'unknown error');
        }
      }
      onDone?.();
    } catch (e) {
      if (e.name === 'AbortError') {
        onDone?.();
      } else {
        onError?.(e.message || String(e));
      }
    }
  })();

  return { cancel: () => ctrl.abort(), promise };
}
